/**
 * 装配体配合文档模型。
 *
 * 文件职责：
 * 1. 管理装配体组件状态与 Mate Definition 列表；
 * 2. 提供可序列化、可重载的纯数据模型；
 * 3. 不持有 Viewer、Object3D、Vue 响应式对象或渲染资源。
 */

import type { AssemblyMateSolverComponentState } from '../solver';
import {
  ASSEMBLY_MATE_SCHEMA_VERSION,
  MateSolveStatus,
  cloneComponentTransformSnapshot,
  type AssemblyReferenceDependencySnapshot,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import { isAssemblyMateAutoAngleReferenceEntity } from '../definition/AssemblyMateAngleReference';
import { canLockConcentricMateRotation } from '../definition/AssemblyMateLockRotation';
import {
  areAssemblyNodeIdentityEquivalent,
  collectAssemblyNodeIdentityLookupIds,
  decodeAssemblyNodeIdSafely,
  normalizeAssemblyNodeId,
  resolveAssemblyMateComponentInstanceIds,
  resolveAssemblyMateOwnerAssemblyNodeId,
  resolveAssemblyMateOwnerEntities,
  splitAssemblyNodePath,
} from '../utils';

/** 装配体 Mate 文档版本。 */
export const ASSEMBLY_MATE_DOCUMENT_SCHEMA_VERSION = 1;

/**
 * 装配体 Mate 文档模型。
 */
export interface AssemblyMateDocumentModel {
  /** 文档协议版本。 */
  readonly schemaVersion: typeof ASSEMBLY_MATE_DOCUMENT_SCHEMA_VERSION;
  /** 所属装配体 ID。 */
  readonly assemblyId: string;
  /** 组件求解状态映射。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** Mate Definition 列表。 */
  readonly mates: readonly AssemblyMateDefinition[];
  /** 创建时间戳。 */
  readonly createdAt: number;
  /** 更新时间戳。 */
  readonly updatedAt: number;
}

/**
 * 可写入项目文件的装配体 Mate JSON。
 *
 * 设计说明：
 * 该类型刻意与运行时文档模型同构，因为 Mate 文档本身已经只包含可序列化数据。
 * 单独声明 JSON 类型可以让对外 API 语义更明确，避免调用方把运行时控制器对象误当作持久化数据。
 */
export interface AssemblyMateDocumentJSON extends AssemblyMateDocumentModel {}

/**
 * 按装配体节点导出或恢复 Mate 的范围。
 *
 * - `ownerOnly`：只处理直接归属于该装配体节点的 Mate；
 * - `subtree`：处理该装配体节点及其所有子装配体节点下的 Mate。
 */
export type AssemblyMateDocumentAssemblyScope = 'ownerOnly' | 'subtree';

/**
 * 按装配体节点定位 Mate 文档范围的输入。
 */
export interface AssemblyMateDocumentAssemblyScopeOptions {
  /** 目标装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 导出或恢复范围；默认只处理目标装配体自身。 */
  readonly scope?: AssemblyMateDocumentAssemblyScope;
}

/**
 * 替换 Mate 文档中某个装配体范围的输入。
 */
export interface ReplaceAssemblyMateDocumentByAssemblyScopeInput extends AssemblyMateDocumentAssemblyScopeOptions {
  /** 被替换的当前完整 Mate 文档。 */
  readonly targetDocument: AssemblyMateDocumentModel;
  /** 用于写入目标范围的新 Mate 文档。 */
  readonly sourceDocument: AssemblyMateDocumentModel;
  /** 当前时间戳，测试可注入固定值。 */
  readonly now?: number;
}

/**
 * 按装配节点子树移除 Mate 引用的输入。
 */
export interface RemoveAssemblyMateDocumentReferencesByAssemblySubtreeInput {
  /** 当前完整 Mate 文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 被删除节点及调用方从节点树逐层收集到的后代节点 ID。 */
  readonly assemblyNodeIds: readonly string[];
  /** 当前时间戳，测试可注入固定值。 */
  readonly now?: number;
}

/**
 * 按装配节点子树移除 Mate 引用的结果。
 */
export interface RemoveAssemblyMateDocumentReferencesByAssemblySubtreeResult {
  /** 移除引用后的完整 Mate 文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 因引用目标节点子树而被移除的 Mate。 */
  readonly removedMates: readonly AssemblyMateDefinition[];
}

/**
 * 批量锁定同轴心旋转的文档更新结果。
 */
export interface LockAssemblyConcentricRotationMatesResult {
  /** 更新后的文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 实际被修改的 Mate ID 列表。 */
  readonly affectedMateIds: readonly string[];
}

/**
 * 批量锁定同轴心旋转的作用域。
 *
 * 设计说明：
 * 设计树中每个装配体/组件节点都可能展示自己的 Mate 文件夹。右键操作只能影响该节点语义下可见的 Mate，
 * 不能把整个顶层文档里的所有同轴心配合一起锁定。
 */
export interface LockAssemblyConcentricRotationMatesScope {
  /** Mate 归属装配体节点 ID；为空时不按装配体归属过滤。 */
  readonly ownerAssemblyNodeId?: string | null;
  /** Mate 参与组件节点 ID；为空时不按组件参与关系过滤。 */
  readonly ownerComponentNodeId?: string | null;
}

/**
 * 创建装配体 Mate 文档输入。
 */
export interface CreateAssemblyMateDocumentInput {
  /** 所属装配体 ID。 */
  readonly assemblyId: string;
  /** 组件求解状态映射。 */
  readonly components?: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 初始 Mate Definition。 */
  readonly mates?: readonly AssemblyMateDefinition[];
  /** 当前时间戳，测试可注入固定值。 */
  readonly now?: number;
}

/**
 * Mate 实体重载引用解析器。
 *
 * 设计说明：
 * document 层不读取 Viewer、Object3D 或 BRep sidecar，只声明“给定持久化 MateEntityRef，返回当前模型下
 * 最新 MateEntityRef”的抽象协议。具体拓扑查找由加载器或 cad-core 更底层的模型模块注入。
 */
export type AssemblyMateEntityReferenceResolver = (entity: MateEntityRef) => MateEntityRef | null;

/**
 * Mate 文档重载引用恢复输入。
 */
export interface RestoreAssemblyMateDocumentReferencesInput {
  /** 已通过 schema 恢复的 Mate 文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 通过稳定拓扑引用或基准引用重新解析当前几何的函数。 */
  readonly resolveEntity: AssemblyMateEntityReferenceResolver;
  /** 当前时间戳，测试可注入固定值。 */
  readonly now?: number;
}

/**
 * Mate 实体恢复结果。
 */
interface RestoredMateEntityResult {
  /** 恢复后的实体。 */
  readonly entity: MateEntityRef;
  /** 缺失引用时的错误信息。 */
  readonly missingReferenceMessage?: string;
}

/**
 * 克隆纯 JSON 数据。
 *
 * @param value 源数据。
 * @returns 克隆结果。
 */
function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * 克隆组件状态。
 *
 * @param component 源组件状态。
 * @returns 组件状态副本。
 */
function cloneComponentState(
  component: AssemblyMateSolverComponentState,
): AssemblyMateSolverComponentState {
  return {
    componentInstanceId: component.componentInstanceId,
    fixationState: component.fixationState,
    transform: cloneComponentTransformSnapshot(component.transform),
  };
}

/**
 * 克隆组件状态映射。
 *
 * @param components 源组件状态映射。
 * @returns 组件状态映射副本。
 */
function cloneComponentStateMap(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): Record<string, AssemblyMateSolverComponentState> {
  const result: Record<string, AssemblyMateSolverComponentState> = {};
  Object.values(components).forEach((component) => {
    result[component.componentInstanceId] = cloneComponentState(component);
  });
  return result;
}

/**
 * 比较两个数值元组是否表示同一 CAD 位姿。
 *
 * @param left 左侧元组。
 * @param right 右侧元组。
 * @param epsilon 数值容差。
 * @returns 元组长度一致且所有分量均在容差内时返回 true。
 */
function areComponentStateNumberTuplesEqual(
  left: readonly number[],
  right: readonly number[],
  epsilon: number,
): boolean {
  return left.length === right.length
    && left.every((value, index) => Math.abs(value - (right[index] ?? Number.NaN)) <= epsilon);
}

/**
 * 判断组件状态映射是否存在可持久化的语义变化。
 *
 * 设计说明：保存入口会从场景重新采样组件位姿。Three.js 矩阵分解可能产生 1e-12 量级浮点噪声，
 * 这类噪声不能刷新 Mate 文档 `updatedAt`，否则未修改场景的连续离线包内容必然漂移。
 *
 * @param left 当前文档组件状态。
 * @param right 本次场景采样组件状态。
 * @returns 组件身份、固定状态与位姿在 CAD 容差内一致时返回 true。
 */
function areAssemblyMateComponentStateMapsEqual(
  left: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  right: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length || leftKeys.some((key, index) => key !== rightKeys[index])) {
    return false;
  }

  const epsilon = 1e-9;
  return leftKeys.every((componentId) => {
    const leftState = left[componentId];
    const rightState = right[componentId];
    if (!leftState || !rightState) {
      return false;
    }
    return leftState.componentInstanceId === rightState.componentInstanceId
      && leftState.fixationState === rightState.fixationState
      && areComponentStateNumberTuplesEqual(
        leftState.transform.position,
        rightState.transform.position,
        epsilon,
      )
      && areComponentStateNumberTuplesEqual(
        leftState.transform.quaternion,
        rightState.transform.quaternion,
        epsilon,
      )
      && areComponentStateNumberTuplesEqual(
        leftState.transform.scale,
        rightState.transform.scale,
        epsilon,
      );
  });
}

/**
 * 克隆 Mate Definition。
 *
 * @param mate 源 Mate Definition。
 * @returns Mate Definition 副本。
 */
export function cloneAssemblyMateDefinition(
  mate: AssemblyMateDefinition,
): AssemblyMateDefinition {
  return cloneSerializable(mate);
}

/**
 * 克隆 Mate Definition 列表。
 *
 * @param mates 源 Mate Definition 列表。
 * @returns Mate Definition 列表副本。
 */
function cloneAssemblyMateDefinitions(
  mates: readonly AssemblyMateDefinition[],
): AssemblyMateDefinition[] {
  return mates.map(cloneAssemblyMateDefinition);
}

/**
 * 按 lowest common parent 规则规范化 Mate 所属装配体。
 *
 * @param mate Mate Definition。
 * @param documentAssemblyId 当前 Mate 文档所属装配体 ID。
 * @returns 归属已规范化的 Mate Definition。
 */
function normalizeAssemblyMateDefinitionOwner(
  mate: AssemblyMateDefinition,
  documentAssemblyId: string,
): AssemblyMateDefinition {
  return cloneAssemblyMateDefinition({
    ...mate,
    assemblyId: resolveAssemblyMateOwnerAssemblyNodeId({
      documentAssemblyId,
      entities: resolveAssemblyMateOwnerEntities(mate),
    }),
  });
}

/**
 * 规范化文档内所有 Mate 的所属装配体。
 *
 * @param document Mate 文档。
 * @returns Mate Definition 列表。
 */
function normalizeAssemblyMateDefinitionOwners(
  document: AssemblyMateDocumentModel,
): AssemblyMateDefinition[] {
  return document.mates.map((mate) => (
    normalizeAssemblyMateDefinitionOwner(mate, document.assemblyId)
  ));
}

/**
 * 判断 Mate 是否属于批量锁定同轴心旋转的目标作用域。
 *
 * @param document Mate 文档。
 * @param mate Mate Definition。
 * @param scope 设计树右键动作传入的作用域。
 * @returns 是否属于当前作用域。
 */
function isAssemblyMateInsideConcentricRotationLockScope(
  document: AssemblyMateDocumentModel,
  mate: AssemblyMateDefinition,
  scope?: LockAssemblyConcentricRotationMatesScope,
): boolean {
  if (!scope) {
    return true;
  }

  const ownerAssemblyNodeId = normalizeAssemblyNodeId(scope.ownerAssemblyNodeId ?? '');
  if (ownerAssemblyNodeId) {
    const mateOwnerAssemblyNodeId = resolveAssemblyMateOwnerAssemblyNodeId({
      documentAssemblyId: document.assemblyId,
      entities: resolveAssemblyMateOwnerEntities(mate),
    });
    if (normalizeAssemblyNodeId(mateOwnerAssemblyNodeId) !== ownerAssemblyNodeId) {
      return false;
    }
  }

  const ownerComponentNodeId = normalizeAssemblyNodeId(scope.ownerComponentNodeId ?? '');
  if (ownerComponentNodeId) {
    return resolveAssemblyMateOwnerEntities(mate).some((entity) => (
      normalizeAssemblyNodeId(entity.assemblyNodeId) === ownerComponentNodeId
    ));
  }

  return true;
}

/**
 * 规范化装配体范围选项。
 *
 * @param input 范围输入。
 * @returns 已规范化的装配体节点 ID 与范围。
 */
function normalizeAssemblyMateDocumentAssemblyScope(
  input: AssemblyMateDocumentAssemblyScopeOptions,
): Required<AssemblyMateDocumentAssemblyScopeOptions> {
  const assemblyNodeId = normalizeAssemblyNodeId(input.assemblyNodeId);
  if (assemblyNodeId.length === 0) {
    throw new Error('装配体配合范围缺少有效 assemblyNodeId。');
  }
  return {
    assemblyNodeId,
    scope: input.scope ?? 'ownerOnly',
  };
}

/**
 * 判断 Mate 是否落在指定装配体范围内。
 *
 * @param mate Mate Definition。
 * @param input 范围输入。
 * @returns 是否属于目标范围。
 */
export function isAssemblyMateDefinitionInAssemblyScope(
  mate: AssemblyMateDefinition,
  input: AssemblyMateDocumentAssemblyScopeOptions,
): boolean {
  const { assemblyNodeId, scope } = normalizeAssemblyMateDocumentAssemblyScope(input);
  const mateAssemblyId = normalizeAssemblyNodeId(mate.assemblyId);
  if (scope === 'ownerOnly') {
    return mateAssemblyId === assemblyNodeId;
  }
  return mateAssemblyId === assemblyNodeId || mateAssemblyId.startsWith(`${assemblyNodeId}/`);
}

/**
 * 判断装配节点 ID 是否已经位于指定装配体子树内。
 *
 * @param assemblyNodeId 待判断的装配节点 ID。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 是否已经位于目标装配体子树内。
 */
function isAssemblyNodeIdInsideAssemblySubtree(
  assemblyNodeId: string,
  rootAssemblyNodeId: string,
): boolean {
  const normalizedAssemblyNodeId = normalizeAssemblyNodeId(assemblyNodeId);
  const normalizedRootAssemblyNodeId = normalizeAssemblyNodeId(rootAssemblyNodeId);
  return normalizedAssemblyNodeId === normalizedRootAssemblyNodeId
    || normalizedAssemblyNodeId.startsWith(`${normalizedRootAssemblyNodeId}/`);
}

/**
 * 将等价但编码形式不同的装配节点锚定到目标子树表示。
 *
 * @param assemblyNodeId 待锚定的装配节点 ID。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 命中等价根前缀时返回目标根加源后缀；否则返回空字符串。
 */
function anchorEquivalentAssemblyNodeIdToAssemblySubtree(
  assemblyNodeId: string,
  rootAssemblyNodeId: string,
): string {
  const normalizedAssemblyNodeId = normalizeAssemblyNodeId(assemblyNodeId);
  const normalizedRootAssemblyNodeId = normalizeAssemblyNodeId(rootAssemblyNodeId);
  const segments = splitAssemblyNodePath(normalizedAssemblyNodeId);

  for (let endIndex = segments.length; endIndex > 0; endIndex -= 1) {
    const sourcePrefix = segments.slice(0, endIndex).join('/');
    if (!areAssemblyNodeIdentityEquivalent(sourcePrefix, normalizedRootAssemblyNodeId)) {
      continue;
    }

    const suffix = segments
      .slice(endIndex)
      .map((segment) => decodeAssemblyNodeIdSafely(segment))
      .filter((segment) => segment.length > 0)
      .join('/');

    return suffix
      ? `${normalizedRootAssemblyNodeId}/${suffix}`
      : normalizedRootAssemblyNodeId;
  }

  return '';
}

/**
 * 创建装配节点子树匹配用 lookup ID。
 *
 * 设计说明：
 * 删除入口会从设计树节点逐层收集子树节点，但 Mate 文档可能混用 URL 编码、解码或去实例索引形式。
 * 这里复用统一身份 lookup，保证删除判断和现有 Mate 恢复/高亮的身份规则一致。
 *
 * @param assemblyNodeIds 被删除节点及其后代节点 ID。
 * @returns 可用于前缀匹配的子树根 lookup ID。
 */
function createAssemblySubtreeLookupIds(assemblyNodeIds: readonly string[]): readonly string[] {
  return assemblyNodeIds
    .flatMap((assemblyNodeId) => collectAssemblyNodeIdentityLookupIds(assemblyNodeId))
    .filter((assemblyNodeId) => assemblyNodeId.length > 0)
    .filter((assemblyNodeId, index, array) => array.indexOf(assemblyNodeId) === index);
}

/**
 * 判断装配节点 ID 是否位于任一删除子树内。
 *
 * @param assemblyNodeId 待判断装配节点 ID。
 * @param subtreeLookupIds 删除子树 lookup ID。
 * @returns 命中任一子树时返回 true。
 */
function isAssemblyNodeIdInsideAnyAssemblySubtree(
  assemblyNodeId: string | null | undefined,
  subtreeLookupIds: readonly string[],
): boolean {
  if (!assemblyNodeId || subtreeLookupIds.length === 0) {
    return false;
  }

  return collectAssemblyNodeIdentityLookupIds(assemblyNodeId).some((candidateLookupId) => (
    subtreeLookupIds.some((subtreeLookupId) => (
      candidateLookupId === subtreeLookupId || candidateLookupId.startsWith(`${subtreeLookupId}/`)
    ))
  ));
}

/**
 * 收集 Mate 实体中会引用装配节点的字段。
 *
 * 设计说明：
 * 删除节点不能只看 componentInstanceId。重载失败、基准引用或拓扑引用可能只在 topologyRef/
 * referenceDependency 中保留旧节点身份；这些字段命中删除子树时也会导致 Mate 悬空。
 *
 * @param entity Mate 实体引用。
 * @returns 实体携带的装配节点 ID。
 */
function collectAssemblyNodeIdsFromMateEntity(entity: MateEntityRef): readonly string[] {
  return [
    entity.assemblyNodeId,
    entity.componentInstanceId,
    entity.topologyRef?.assemblyNodeId,
    ...(entity.referenceDependency?.driverComponentInstanceIds ?? []),
    ...(entity.referenceDependency?.driverTopologyRefs.map((ref) => ref.assemblyNodeId) ?? []),
  ]
    .filter((assemblyNodeId): assemblyNodeId is string => (
      typeof assemblyNodeId === 'string' && assemblyNodeId.trim().length > 0
    ));
}

/**
 * 解析删除子树时需要检查的 Mate 实体引用。
 *
 * 设计说明：
 * 自动角度参考实体由 cad-core 派生，不代表用户选择的第三个组件，不能扩大删除影响范围；
 * 手动 referenceEntity 则来自用户选择，命中删除子树时必须移除该 Mate。
 *
 * @param mate Mate Definition。
 * @returns 需要参与删除命中的实体引用。
 */
function resolveAssemblyMateDeletionReferenceEntities(
  mate: AssemblyMateDefinition,
): readonly MateEntityRef[] {
  return [
    mate.firstEntity,
    mate.secondEntity,
    ...(mate.referenceEntity && !isAssemblyMateAutoAngleReferenceEntity(mate.referenceEntity)
      ? [mate.referenceEntity]
      : []),
  ];
}

/**
 * 判断 Mate 是否引用了任一删除装配子树。
 *
 * @param mate Mate Definition。
 * @param subtreeLookupIds 删除子树 lookup ID。
 * @returns 命中删除子树时返回 true。
 */
function isAssemblyMateReferencingAssemblySubtrees(
  mate: AssemblyMateDefinition,
  subtreeLookupIds: readonly string[],
): boolean {
  if (isAssemblyNodeIdInsideAnyAssemblySubtree(mate.assemblyId, subtreeLookupIds)) {
    return true;
  }

  return resolveAssemblyMateDeletionReferenceEntities(mate).some((entity) => (
    collectAssemblyNodeIdsFromMateEntity(entity).some((assemblyNodeId) => (
      isAssemblyNodeIdInsideAnyAssemblySubtree(assemblyNodeId, subtreeLookupIds)
    ))
  ));
}

/**
 * 将局部 Mate 文档内的组件节点 ID 锚定到目标装配体子树。
 *
 * 设计说明：
 * 子装配独立打开时，Mate 实体可能只保存 `Part1:1` 这样的文件内相对 ID；
 * 但它被恢复到父工作区后，完整 Mate 文档必须使用 `Root/SubAsm/Part1:1`，
 * 否则最低公共父装配体规则会把 Mate 误判为根装配体所有。
 *
 * @param assemblyNodeId 局部文档中的装配节点 ID。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定到目标装配体子树的装配节点 ID。
 */
function anchorAssemblyNodeIdToAssemblySubtree(
  assemblyNodeId: string,
  rootAssemblyNodeId: string,
): string {
  const normalizedAssemblyNodeId = normalizeAssemblyNodeId(assemblyNodeId);
  const normalizedRootAssemblyNodeId = normalizeAssemblyNodeId(rootAssemblyNodeId);
  if (!normalizedAssemblyNodeId || !normalizedRootAssemblyNodeId) {
    return normalizedAssemblyNodeId;
  }
  if (isAssemblyNodeIdInsideAssemblySubtree(normalizedAssemblyNodeId, normalizedRootAssemblyNodeId)) {
    return normalizedAssemblyNodeId;
  }
  const equivalentAnchoredNodeId = anchorEquivalentAssemblyNodeIdToAssemblySubtree(
    normalizedAssemblyNodeId,
    normalizedRootAssemblyNodeId,
  );
  if (equivalentAnchoredNodeId) {
    return equivalentAnchoredNodeId;
  }
  return `${normalizedRootAssemblyNodeId}/${normalizedAssemblyNodeId}`;
}

/**
 * 将 stableRef 中的装配节点前缀锚定到目标装配体子树。
 *
 * @param stableRef 原始稳定引用。
 * @param sourceAssemblyNodeId stableRef 原本对应的装配节点 ID。
 * @param anchoredAssemblyNodeId 锚定后的装配节点 ID。
 * @returns 已重写前缀的稳定引用。
 */
function anchorAssemblyMateStableRefToAssemblySubtree(
  stableRef: string,
  sourceAssemblyNodeId: string,
  anchoredAssemblyNodeId: string,
): string {
  const normalizedSourceAssemblyNodeId = normalizeAssemblyNodeId(sourceAssemblyNodeId);
  if (!stableRef || !normalizedSourceAssemblyNodeId || normalizedSourceAssemblyNodeId === anchoredAssemblyNodeId) {
    return stableRef;
  }
  if (stableRef === normalizedSourceAssemblyNodeId) {
    return anchoredAssemblyNodeId;
  }
  if (stableRef.startsWith(`${normalizedSourceAssemblyNodeId}:`)) {
    return `${anchoredAssemblyNodeId}${stableRef.slice(normalizedSourceAssemblyNodeId.length)}`;
  }
  return stableRef;
}

/**
 * 替换装配节点 ID 的路径前缀。
 *
 * @param assemblyNodeId 原始装配节点 ID。
 * @param sourceAssemblyNodeId 需要替换的源前缀。
 * @param targetAssemblyNodeId 替换后的目标前缀。
 * @returns 已替换前缀的装配节点 ID。
 */
function replaceAssemblyNodeIdPrefix(
  assemblyNodeId: string,
  sourceAssemblyNodeId: string,
  targetAssemblyNodeId: string,
): string {
  const normalizedAssemblyNodeId = normalizeAssemblyNodeId(assemblyNodeId);
  const normalizedSourceAssemblyNodeId = normalizeAssemblyNodeId(sourceAssemblyNodeId);
  const normalizedTargetAssemblyNodeId = normalizeAssemblyNodeId(targetAssemblyNodeId);
  if (!normalizedAssemblyNodeId || !normalizedSourceAssemblyNodeId || !normalizedTargetAssemblyNodeId) {
    return normalizedAssemblyNodeId;
  }
  if (
    normalizedAssemblyNodeId === normalizedTargetAssemblyNodeId
    || normalizedAssemblyNodeId.startsWith(`${normalizedTargetAssemblyNodeId}/`)
  ) {
    return normalizedAssemblyNodeId;
  }
  if (normalizedAssemblyNodeId === normalizedSourceAssemblyNodeId) {
    return normalizedTargetAssemblyNodeId;
  }
  if (normalizedAssemblyNodeId.startsWith(`${normalizedSourceAssemblyNodeId}/`)) {
    return `${normalizedTargetAssemblyNodeId}${normalizedAssemblyNodeId.slice(normalizedSourceAssemblyNodeId.length)}`;
  }
  return normalizedAssemblyNodeId;
}

/**
 * 将拓扑选择引用锚定到目标装配体子树。
 *
 * @param ref 原始拓扑引用。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定的拓扑引用。
 */
function anchorAssemblyTopologySelectionRefToAssemblySubtree(
  ref: AssemblyTopologySelectionRef,
  rootAssemblyNodeId: string,
): AssemblyTopologySelectionRef {
  return {
    ...cloneSerializable(ref),
    assemblyNodeId: anchorAssemblyNodeIdToAssemblySubtree(ref.assemblyNodeId, rootAssemblyNodeId),
  };
}

/**
 * 将基准引用依赖锚定到目标装配体子树。
 *
 * @param dependency 原始基准引用依赖。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定的基准引用依赖。
 */
function anchorAssemblyReferenceDependencyToAssemblySubtree(
  dependency: AssemblyReferenceDependencySnapshot,
  rootAssemblyNodeId: string,
): AssemblyReferenceDependencySnapshot {
  return {
    ...cloneSerializable(dependency),
    driverComponentInstanceIds: dependency.driverComponentInstanceIds.map((componentInstanceId) => (
      anchorAssemblyNodeIdToAssemblySubtree(componentInstanceId, rootAssemblyNodeId)
    )),
    driverTopologyRefs: dependency.driverTopologyRefs.map((ref) => (
      anchorAssemblyTopologySelectionRefToAssemblySubtree(ref, rootAssemblyNodeId)
    )),
  };
}

/**
 * 将 Mate 实体引用锚定到目标装配体子树。
 *
 * @param entity 原始 Mate 实体引用。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定的 Mate 实体引用。
 */
function anchorAssemblyMateEntityToAssemblySubtree(
  entity: MateEntityRef,
  rootAssemblyNodeId: string,
): MateEntityRef {
  const anchoredAssemblyNodeId = anchorAssemblyNodeIdToAssemblySubtree(
    entity.assemblyNodeId,
    rootAssemblyNodeId,
  );

  return {
    ...cloneSerializable(entity),
    stableRef: anchorAssemblyMateStableRefToAssemblySubtree(
      entity.stableRef,
      entity.assemblyNodeId,
      anchoredAssemblyNodeId,
    ),
    assemblyNodeId: anchoredAssemblyNodeId,
    componentInstanceId: anchorAssemblyNodeIdToAssemblySubtree(
      entity.componentInstanceId,
      rootAssemblyNodeId,
    ),
    topologyRef: entity.topologyRef
      ? anchorAssemblyTopologySelectionRefToAssemblySubtree(entity.topologyRef, rootAssemblyNodeId)
      : undefined,
    referenceDependency: entity.referenceDependency
      ? anchorAssemblyReferenceDependencyToAssemblySubtree(entity.referenceDependency, rootAssemblyNodeId)
      : undefined,
  };
}

/**
 * 将 Mate Definition 中的实体引用锚定到目标装配体子树。
 *
 * @param mate 原始 Mate Definition。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定且重新归一化 owner 的 Mate Definition。
 */
function anchorAssemblyMateDefinitionToAssemblySubtree(
  mate: AssemblyMateDefinition,
  rootAssemblyNodeId: string,
): AssemblyMateDefinition {
  const anchoredMate = cloneAssemblyMateDefinition({
    ...mate,
    firstEntity: anchorAssemblyMateEntityToAssemblySubtree(mate.firstEntity, rootAssemblyNodeId),
    secondEntity: anchorAssemblyMateEntityToAssemblySubtree(mate.secondEntity, rootAssemblyNodeId),
    ...(mate.referenceEntity
      ? { referenceEntity: anchorAssemblyMateEntityToAssemblySubtree(mate.referenceEntity, rootAssemblyNodeId) }
      : {}),
  });

  return normalizeAssemblyMateDefinitionOwner(anchoredMate, rootAssemblyNodeId);
}

/**
 * 将组件状态锚定到目标装配体子树。
 *
 * @param component 原始组件状态。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 已锚定的组件状态。
 */
function anchorAssemblyMateComponentStateToAssemblySubtree(
  component: AssemblyMateSolverComponentState,
  rootAssemblyNodeId: string,
): AssemblyMateSolverComponentState {
  return cloneComponentState({
    ...component,
    componentInstanceId: anchorAssemblyNodeIdToAssemblySubtree(
      component.componentInstanceId,
      rootAssemblyNodeId,
    ),
  });
}

/**
 * 将局部 Mate 文档锚定到目标装配体子树。
 *
 * @param document 已序列化的局部 Mate 文档。
 * @param rootAssemblyNodeId 目标装配体根节点 ID。
 * @returns 可合并进完整装配 Mate 文档的局部文档。
 */
function anchorAssemblyMateDocumentToAssemblySubtree(
  document: AssemblyMateDocumentModel,
  rootAssemblyNodeId: string,
): AssemblyMateDocumentModel {
  const anchoredComponents: Record<string, AssemblyMateSolverComponentState> = {};
  Object.values(document.components).forEach((component) => {
    const anchoredComponent = anchorAssemblyMateComponentStateToAssemblySubtree(
      component,
      rootAssemblyNodeId,
    );
    anchoredComponents[anchoredComponent.componentInstanceId] = anchoredComponent;
  });

  return {
    ...document,
    assemblyId: normalizeAssemblyNodeId(rootAssemblyNodeId),
    components: anchoredComponents,
    mates: document.mates.map((mate) => (
      anchorAssemblyMateDefinitionToAssemblySubtree(mate, rootAssemblyNodeId)
    )),
  };
}

/**
 * 解析 Mate 列表参与的组件实例 ID。
 *
 * @param mates Mate Definition 列表。
 * @returns 组件实例 ID 集合。
 */
function resolveAssemblyMateComponentIds(
  mates: readonly AssemblyMateDefinition[],
): Set<string> {
  const componentIds = new Set<string>();
  mates.forEach((mate) => {
    resolveAssemblyMateComponentInstanceIds(mate).forEach((componentId) => {
      componentIds.add(componentId);
    });
  });
  return componentIds;
}

/**
 * 断言 Mate 列表中的稳定身份没有重复。
 *
 * scoped 文档恢复会跨 occurrence 路径迁移同一业务 Mate；`mateId` 是该过程唯一不随路径变化的
 * 稳定身份，因此源文档和合并结果都必须满足唯一性，禁止把歧义数据带入 controller。
 *
 * @param mates 待检查 Mate Definition。
 * @param context 错误上下文。
 */
function assertAssemblyMateDefinitionIdsUnique(
  mates: readonly AssemblyMateDefinition[],
  context: string,
): void {
  const mateIds = new Set<string>();
  mates.forEach((mate) => {
    if (mateIds.has(mate.mateId)) {
      throw new Error(`${context}包含重复 Mate 身份：${mate.mateId}`);
    }
    mateIds.add(mate.mateId);
  });
}

/**
 * 按组件 ID 过滤组件状态映射。
 *
 * @param components 源组件状态映射。
 * @param componentIds 需要保留的组件 ID。
 * @returns 组件状态映射副本。
 */
function filterComponentStateMapByComponentIds(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  componentIds: ReadonlySet<string>,
): Record<string, AssemblyMateSolverComponentState> {
  const result: Record<string, AssemblyMateSolverComponentState> = {};
  Object.values(components).forEach((component) => {
    if (componentIds.has(component.componentInstanceId)) {
      result[component.componentInstanceId] = cloneComponentState(component);
    }
  });
  return result;
}

/**
 * 创建装配体 Mate 文档。
 *
 * @param input 创建输入。
 * @returns Mate 文档模型。
 */
export function createAssemblyMateDocument(
  input: CreateAssemblyMateDocumentInput,
): AssemblyMateDocumentModel {
  const now = input.now ?? Date.now();
  return {
    schemaVersion: ASSEMBLY_MATE_DOCUMENT_SCHEMA_VERSION,
    assemblyId: input.assemblyId,
    components: cloneComponentStateMap(input.components ?? {}),
    mates: cloneAssemblyMateDefinitions(input.mates ?? []),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * 替换文档组件状态。
 *
 * @param document 源文档。
 * @param components 新组件状态映射。
 * @param now 当前时间戳。
 * @returns 新文档。
 */
export function setAssemblyMateDocumentComponents(
  document: AssemblyMateDocumentModel,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  if (areAssemblyMateComponentStateMapsEqual(document.components, components)) {
    return serializeAssemblyMateDocument(document);
  }

  return {
    ...document,
    components: cloneComponentStateMap(components),
    updatedAt: now,
  };
}

/**
 * 新增 Mate Definition。
 *
 * @param document 源文档。
 * @param mate Mate Definition。
 * @param now 当前时间戳。
 * @returns 新文档。
 * @throws 当文档中已存在相同 Mate ID 时抛出错误。
 */
export function addAssemblyMateDefinition(
  document: AssemblyMateDocumentModel,
  mate: AssemblyMateDefinition,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  const exists = document.mates.some((item) => item.mateId === mate.mateId);
  if (exists) {
    throw new Error(`装配体配合已存在，无法重复新增：${mate.mateId}`);
  }

  return {
    ...document,
    mates: [
      ...cloneAssemblyMateDefinitions(document.mates),
      cloneAssemblyMateDefinition(mate),
    ],
    updatedAt: now,
  };
}

/**
 * 编辑 Mate Definition。
 *
 * @param document 源文档。
 * @param mateId Mate ID。
 * @param updater 更新函数。
 * @param now 当前时间戳。
 * @returns 新文档。
 * @throws 当文档中不存在指定 Mate ID 时抛出错误。
 */
export function editAssemblyMateDefinition(
  document: AssemblyMateDocumentModel,
  mateId: string,
  updater: (mate: AssemblyMateDefinition) => AssemblyMateDefinition,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  let matched = false;
  const mates = document.mates.map((mate) => {
    if (mate.mateId !== mateId) {
      return cloneAssemblyMateDefinition(mate);
    }
    matched = true;
    return cloneAssemblyMateDefinition(updater(cloneAssemblyMateDefinition(mate)));
  });

  if (!matched) {
    throw new Error(`无法编辑装配体配合，未找到 Mate：${mateId}`);
  }

  return {
    ...document,
    mates,
    updatedAt: now,
  };
}

/**
 * 删除 Mate Definition。
 *
 * @param document 源文档。
 * @param mateId Mate ID。
 * @param now 当前时间戳。
 * @returns 新文档。
 * @throws 当文档中不存在指定 Mate ID 时抛出错误。
 */
export function deleteAssemblyMateDefinition(
  document: AssemblyMateDocumentModel,
  mateId: string,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  const mates = document.mates.filter((mate) => mate.mateId !== mateId);
  if (mates.length === document.mates.length) {
    throw new Error(`无法删除装配体配合，未找到 Mate：${mateId}`);
  }

  return {
    ...document,
    mates: cloneAssemblyMateDefinitions(mates),
    updatedAt: now,
  };
}

/**
 * 抑制或解除抑制 Mate Definition。
 *
 * @param document 源文档。
 * @param mateId Mate ID。
 * @param suppressed 是否抑制。
 * @param now 当前时间戳。
 * @returns 新文档。
 */
export function suppressAssemblyMateDefinition(
  document: AssemblyMateDocumentModel,
  mateId: string,
  suppressed: boolean,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  return editAssemblyMateDefinition(
    document,
    mateId,
    (mate) => ({
      ...mate,
      suppressed,
      solveStatus: suppressed ? MateSolveStatus.NotRun : mate.solveStatus,
      updatedAt: now,
    }),
    now,
  );
}

/**
 * 批量锁定当前文档内所有可锁定的同轴心 Mate。
 *
 * 设计说明：
 * SOLIDWORKS 在设计树 `Mates` 文件夹右键提供 `Lock Concentric Rotation`，
 * 该命令只应修改真正具有公共轴旋转自由度的 Concentric Mate。
 *
 * @param document 源 Mate 文档。
 * @param now 当前时间戳。
 * @param scope 设计树节点作用域；为空时处理整个文档。
 * @returns 更新结果与受影响 Mate ID。
 */
export function lockAssemblyConcentricRotationMates(
  document: AssemblyMateDocumentModel,
  now: number = Date.now(),
  scope?: LockAssemblyConcentricRotationMatesScope,
): LockAssemblyConcentricRotationMatesResult {
  const affectedMateIds: string[] = [];
  const mates = document.mates.map((mate) => {
    if (
      !canLockConcentricMateRotation(mate)
      || !isAssemblyMateInsideConcentricRotationLockScope(document, mate, scope)
    ) {
      return cloneAssemblyMateDefinition(mate);
    }

    affectedMateIds.push(mate.mateId);
    return cloneAssemblyMateDefinition({
      ...mate,
      lockRotation: true,
      updatedAt: now,
    });
  });

  if (affectedMateIds.length === 0) {
    return {
      document: serializeAssemblyMateDocument(document),
      affectedMateIds,
    };
  }

  return {
    document: {
      ...document,
      mates,
      updatedAt: now,
    },
    affectedMateIds,
  };
}

/**
 * 更新 Mate 求解状态。
 *
 * @param document 源文档。
 * @param mateId Mate ID。
 * @param status 求解状态。
 * @param errorMessage 错误信息。
 * @param now 当前时间戳。
 * @returns 新文档。
 */
export function setAssemblyMateSolveStatus(
  document: AssemblyMateDocumentModel,
  mateId: string,
  status: MateSolveStatus,
  errorMessage?: string,
  now: number = Date.now(),
): AssemblyMateDocumentModel {
  /**
   * Solved 是文档级成功状态，不能继续携带旧错误文案；否则设计树会把已满足 Mate 误判为异常。
   */
  const normalizedErrorMessage = status === MateSolveStatus.Solved
    ? undefined
    : errorMessage;
  return editAssemblyMateDefinition(
    document,
    mateId,
    (mate) => ({
      ...mate,
      solveStatus: status,
      errorMessage: normalizedErrorMessage,
      updatedAt: now,
    }),
    now,
  );
}

/**
 * 序列化装配体 Mate 文档。
 *
 * @param document 源文档。
 * @returns 可写入项目文件的纯数据。
 */
export function serializeAssemblyMateDocument(
  document: AssemblyMateDocumentModel,
): AssemblyMateDocumentModel {
  return {
    ...document,
    components: cloneComponentStateMap(document.components),
    mates: cloneAssemblyMateDefinitions(document.mates),
  };
}

/**
 * 将 Mate 文档转换为可持久化 JSON。
 *
 * 设计说明：
 * 1. 保存链路必须输出纯 JSON 数据，不能暴露 Viewer、Object3D 或 Vue 响应式对象；
 * 2. 每条 Mate 的 `assemblyId` 在保存前按 SOLIDWORKS lowest common parent 规则归一化；
 * 3. 该函数不解析 BRep，也不触碰运行时场景，避免保存动作污染预览状态。
 *
 * @param document Mate 文档。
 * @returns 可写入项目文件的 Mate JSON。
 */
export function toAssemblyMateDocumentJSON(
  document: AssemblyMateDocumentModel,
): AssemblyMateDocumentJSON {
  const serializedDocument = serializeAssemblyMateDocument(document);
  return {
    ...serializedDocument,
    mates: normalizeAssemblyMateDefinitionOwners(serializedDocument),
  };
}

/**
 * 将 Mate 文档按指定装配体范围转换为可持久化 JSON。
 *
 * 设计说明：
 * 该函数用于子装配体独立保存或外部系统只读取局部 Mate 数据的场景。范围过滤只依据
 * 已归一化的 Mate `assemblyId`，不会读取场景树或 UI 状态。
 *
 * @param document Mate 文档。
 * @param options 装配体范围选项。
 * @returns 指定范围内的 Mate 文档 JSON。
 */
export function toAssemblyMateDocumentJSONByAssembly(
  document: AssemblyMateDocumentModel,
  options: AssemblyMateDocumentAssemblyScopeOptions,
): AssemblyMateDocumentJSON {
  const { assemblyNodeId } = normalizeAssemblyMateDocumentAssemblyScope(options);
  const serializedDocument = toAssemblyMateDocumentJSON(document);
  const scopedMates = serializedDocument.mates.filter((mate) => (
    isAssemblyMateDefinitionInAssemblyScope(mate, options)
  ));
  const componentIds = resolveAssemblyMateComponentIds(scopedMates);

  return {
    ...serializedDocument,
    assemblyId: assemblyNodeId,
    components: filterComponentStateMapByComponentIds(serializedDocument.components, componentIds),
    mates: cloneAssemblyMateDefinitions(scopedMates),
  };
}

/**
 * 替换 Mate 文档中指定装配体范围的 Mate 数据。
 *
 * 设计说明：
 * 1. 用于 `fromJSONByAssembly` 的底层纯函数；
 * 2. 目标范围内的旧 Mate 会被 sourceDocument 中同范围 Mate 替换；
 * 3. 范围外 Mate 和仍被范围外 Mate 使用的组件快照会保留；
 * 4. 传入 JSON 必须明确属于同一个目标装配体，避免把其它装配体数据误写入当前范围。
 *
 * @param input 替换输入。
 * @returns 替换后的完整 Mate 文档。
 * @throws 当来源文档的装配体 ID 与目标作用域不一致时抛出错误。
 */
export function replaceAssemblyMateDocumentByAssemblyScope(
  input: ReplaceAssemblyMateDocumentByAssemblyScopeInput,
): AssemblyMateDocumentModel {
  const scopeOptions = normalizeAssemblyMateDocumentAssemblyScope(input);
  const targetDocument = toAssemblyMateDocumentJSON(input.targetDocument);
  const serializedSourceDocument = toAssemblyMateDocumentJSON(input.sourceDocument);
  const sourceAssemblyId = normalizeAssemblyNodeId(serializedSourceDocument.assemblyId);
  if (sourceAssemblyId !== scopeOptions.assemblyNodeId) {
    throw new Error(
      `装配体配合 JSON assemblyId 与目标装配体不一致：${sourceAssemblyId} !== ${scopeOptions.assemblyNodeId}`,
    );
  }

  const sourceDocument = anchorAssemblyMateDocumentToAssemblySubtree(
    serializedSourceDocument,
    scopeOptions.assemblyNodeId,
  );

  const sourceMates = sourceDocument.mates.filter((mate) => (
    isAssemblyMateDefinitionInAssemblyScope(mate, scopeOptions)
  ));
  assertAssemblyMateDefinitionIdsUnique(sourceMates, '装配体 scoped Mate 源文档');
  const sourceMateIds = new Set(sourceMates.map((mate) => mate.mateId));
  const retainedMates: AssemblyMateDefinition[] = [];
  const removedMates: AssemblyMateDefinition[] = [];
  targetDocument.mates.forEach((mate) => {
    // occurrence 路径迁移后旧 Mate 已无法命中新 scope；同 mateId 仍可证明它是 source 的旧版本。
    if (
      isAssemblyMateDefinitionInAssemblyScope(mate, scopeOptions)
      || sourceMateIds.has(mate.mateId)
    ) {
      removedMates.push(mate);
      return;
    }
    retainedMates.push(mate);
  });
  const nextMates = [
    ...retainedMates,
    ...sourceMates,
  ];
  assertAssemblyMateDefinitionIdsUnique(nextMates, '装配体 scoped Mate 合并结果');
  const retainedComponentIds = resolveAssemblyMateComponentIds(retainedMates);
  const removedComponentIds = resolveAssemblyMateComponentIds(removedMates);
  const sourceComponentIds = resolveAssemblyMateComponentIds(sourceMates);
  const components = cloneComponentStateMap(targetDocument.components);

  removedComponentIds.forEach((componentId) => {
    if (!retainedComponentIds.has(componentId)) {
      delete components[componentId];
    }
  });
  Object.assign(
    components,
    filterComponentStateMapByComponentIds(sourceDocument.components, sourceComponentIds),
  );

  return {
    ...targetDocument,
    components,
    mates: cloneAssemblyMateDefinitions(nextMates),
    updatedAt: input.now ?? Date.now(),
  };
}

/**
 * 从指定装配节点子树移除所有悬挂的 Mate 引用。
 *
 * 设计说明：
 * 删除/切换装配节点时，旧节点子树会整体离开当前工程；任何直接归属于该子树、
 * 或任一参与实体/手动参考实体指向该子树的 Mate 都会变成悬挂引用。这里只处理
 * 纯 Mate 文档数据，不触碰 Viewer/Three 对象，供删除弹框、历史恢复和 GLB 加载链路复用。
 *
 * @param input 删除子树与当前 Mate 文档。
 * @returns 清理后的 Mate 文档，以及被移除的 Mate 列表。
 */
export function removeAssemblyMateDocumentReferencesByAssemblySubtree(
  input: RemoveAssemblyMateDocumentReferencesByAssemblySubtreeInput,
): RemoveAssemblyMateDocumentReferencesByAssemblySubtreeResult {
  const subtreeLookupIds = createAssemblySubtreeLookupIds(input.assemblyNodeIds);
  const targetDocument = toAssemblyMateDocumentJSON(input.document);
  if (subtreeLookupIds.length === 0) {
    return {
      document: serializeAssemblyMateDocument(targetDocument),
      removedMates: [],
    };
  }

  const retainedMates: AssemblyMateDefinition[] = [];
  const removedMates: AssemblyMateDefinition[] = [];
  targetDocument.mates.forEach((mate) => {
    if (isAssemblyMateReferencingAssemblySubtrees(mate, subtreeLookupIds)) {
      removedMates.push(mate);
      return;
    }
    retainedMates.push(mate);
  });

  if (removedMates.length === 0) {
    return {
      document: serializeAssemblyMateDocument(targetDocument),
      removedMates: [],
    };
  }

  const retainedComponentIds = resolveAssemblyMateComponentIds(retainedMates);
  return {
    document: {
      ...targetDocument,
      components: filterComponentStateMapByComponentIds(targetDocument.components, retainedComponentIds),
      mates: cloneAssemblyMateDefinitions(retainedMates),
      updatedAt: input.now ?? Date.now(),
    },
    removedMates: cloneAssemblyMateDefinitions(removedMates),
  };
}

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 断言外部输入符合 Mate 文档 JSON 的基本结构。
 *
 * @param data 外部 JSON 数据。
 */
function assertAssemblyMateDocumentJSON(data: unknown): asserts data is AssemblyMateDocumentJSON {
  if (!isRecord(data)) {
    throw new Error('装配体配合 JSON 必须是对象。');
  }
  if (data.schemaVersion !== ASSEMBLY_MATE_DOCUMENT_SCHEMA_VERSION) {
    throw new Error(`不支持的装配体配合 JSON 版本：${String(data.schemaVersion)}`);
  }
  if (typeof data.assemblyId !== 'string' || data.assemblyId.trim().length === 0) {
    throw new Error('装配体配合 JSON 缺少有效 assemblyId。');
  }
  if (!isRecord(data.components)) {
    throw new Error('装配体配合 JSON.components 必须是对象。');
  }
  if (!Array.isArray(data.mates)) {
    throw new Error('装配体配合 JSON.mates 必须是数组。');
  }
  data.mates.forEach((mate, index) => {
    if (!isRecord(mate)) {
      throw new Error(`装配体配合 JSON.mates[${index}] 必须是对象。`);
    }
    if (mate.schemaVersion !== ASSEMBLY_MATE_SCHEMA_VERSION) {
      throw new Error(`不支持的装配体配合定义版本：${String(mate.schemaVersion)}`);
    }
  });
  if (typeof data.createdAt !== 'number' || !Number.isFinite(data.createdAt)) {
    throw new Error('装配体配合 JSON.createdAt 必须是有限数字。');
  }
  if (typeof data.updatedAt !== 'number' || !Number.isFinite(data.updatedAt)) {
    throw new Error('装配体配合 JSON.updatedAt 必须是有限数字。');
  }
}

/**
 * 从项目 JSON 恢复 Mate 文档。
 *
 * 设计说明：
 * 该函数只恢复 schema 与纯数据结构，不刷新实体几何引用；重载模型后需要再由控制器
 * 通过 `restoreAssemblyMateDocumentReferences` 注入当前模型的引用解析器。
 *
 * @param data 项目文件中的 Mate JSON。
 * @returns 恢复后的 Mate 文档模型。
 */
export function fromAssemblyMateDocumentJSON(data: unknown): AssemblyMateDocumentModel {
  assertAssemblyMateDocumentJSON(data);
  return restoreAssemblyMateDocument(data);
}

/**
 * 恢复单个 Mate 实体引用。
 *
 * @param entity 持久化实体引用。
 * @param resolveEntity 当前模型稳定引用解析函数。
 * @returns 恢复结果。
 */
/**
 * 判断 resolved 节点是否只是 document 节点的相对别名。
 *
 * @param documentNodeId Mate 文档中已经锚定的节点 ID。
 * @param resolvedNodeId 当前模型解析出的节点 ID。
 * @returns resolved 只是 document 的自身或尾部相对路径时返回 true。
 */
function isAssemblyMateResolvedNodeRelativeAlias(
  documentNodeId: string,
  resolvedNodeId: string,
): boolean {
  const normalizedDocumentNodeId = normalizeAssemblyNodeId(documentNodeId);
  const normalizedResolvedNodeId = normalizeAssemblyNodeId(resolvedNodeId);
  if (!normalizedDocumentNodeId || !normalizedResolvedNodeId) {
    return false;
  }

  return areAssemblyNodeIdentityEquivalent(normalizedDocumentNodeId, normalizedResolvedNodeId)
    || normalizedDocumentNodeId === normalizedResolvedNodeId
    || normalizedDocumentNodeId.endsWith(`/${normalizedResolvedNodeId}`);
}

/**
 * 判断引用恢复结果是否应该采用 resolver 的组件归属身份。
 *
 * 设计说明：
 * `restoreReferences` 需要同时保护两类场景：
 * 1. 子装配 `.puppy` 被挂到父工作区后，resolver 可能从离线包对象反推回相对 ID，此时必须保留文档锚定身份；
 * 2. 导入 body 被折叠到父组件时，bodyBindingSelectionTargets 会把真实可移动边界改成父组件，此时必须采用 resolver 身份。
 *
 * @param documentEntity Mate 文档中保存的实体引用。
 * @param resolvedEntity 当前模型刷新后的实体引用。
 * @returns 是否使用 resolver 的组件身份。
 */
function shouldUseResolvedAssemblyMateEntityIdentity(
  documentEntity: MateEntityRef,
  resolvedEntity: MateEntityRef,
): boolean {
  const documentBodyBindingId = documentEntity.topologyRef?.bodyBindingId?.trim();
  const resolvedBodyBindingId = resolvedEntity.topologyRef?.bodyBindingId?.trim();
  if (!documentBodyBindingId || documentBodyBindingId !== resolvedBodyBindingId) {
    return false;
  }

  const assemblyIsRelativeAlias = isAssemblyMateResolvedNodeRelativeAlias(
    documentEntity.assemblyNodeId,
    resolvedEntity.assemblyNodeId,
  );
  const componentIsRelativeAlias = isAssemblyMateResolvedNodeRelativeAlias(
    documentEntity.componentInstanceId,
    resolvedEntity.componentInstanceId,
  );

  return !assemblyIsRelativeAlias || !componentIsRelativeAlias;
}

/**
 * 保留 Mate 文档中已经确定的实体归属身份。
 *
 * 设计说明：
 * `restoreReferences` 的职责是刷新当前模型中的几何和拓扑引用，不应随意重算 Mate 所属装配体。
 * 子装配恢复到父工作区时，相对离线包 ID 要合并回文档锚定节点；但 bodyBinding 明确命中父组件边界时，
 * resolver 才是当前模型真实可移动边界，必须保留 resolver 的组件身份。
 *
 * @param documentEntity Mate 文档中保存的实体引用。
 * @param resolvedEntity resolver 刷新后的实体引用。
 * @returns 几何已刷新且组件身份已按恢复语义合并后的实体引用。
 */
function preserveAssemblyMateDocumentEntityIdentity(
  documentEntity: MateEntityRef,
  resolvedEntity: MateEntityRef,
): MateEntityRef {
  const documentAssemblyNodeId = normalizeAssemblyNodeId(documentEntity.assemblyNodeId);
  const documentComponentInstanceId = normalizeAssemblyNodeId(documentEntity.componentInstanceId);
  const resolvedAssemblyNodeId = normalizeAssemblyNodeId(resolvedEntity.assemblyNodeId);
  const resolvedComponentInstanceId = normalizeAssemblyNodeId(resolvedEntity.componentInstanceId);
  if (shouldUseResolvedAssemblyMateEntityIdentity(documentEntity, resolvedEntity)) {
    return cloneSerializable(resolvedEntity);
  }

  return {
    ...cloneSerializable(resolvedEntity),
    stableRef: anchorAssemblyMateStableRefToAssemblySubtree(
      resolvedEntity.stableRef,
      resolvedAssemblyNodeId,
      documentAssemblyNodeId,
    ),
    assemblyNodeId: documentAssemblyNodeId,
    componentInstanceId: documentComponentInstanceId,
    topologyRef: resolvedEntity.topologyRef
      ? {
          ...cloneSerializable(resolvedEntity.topologyRef),
          assemblyNodeId: replaceAssemblyNodeIdPrefix(
            resolvedEntity.topologyRef.assemblyNodeId,
            resolvedAssemblyNodeId,
            documentAssemblyNodeId,
          ),
        }
      : undefined,
    referenceDependency: resolvedEntity.referenceDependency
      ? {
          ...cloneSerializable(resolvedEntity.referenceDependency),
          driverComponentInstanceIds: resolvedEntity.referenceDependency.driverComponentInstanceIds.map((componentId) => (
            replaceAssemblyNodeIdPrefix(componentId, resolvedComponentInstanceId, documentComponentInstanceId)
          )),
          driverTopologyRefs: resolvedEntity.referenceDependency.driverTopologyRefs.map((ref) => ({
            ...cloneSerializable(ref),
            assemblyNodeId: replaceAssemblyNodeIdPrefix(
              ref.assemblyNodeId,
              resolvedAssemblyNodeId,
              documentAssemblyNodeId,
            ),
          })),
        }
      : undefined,
  };
}

function restoreMateEntityReference(
  entity: MateEntityRef,
  resolveEntity: AssemblyMateEntityReferenceResolver,
): RestoredMateEntityResult {
  if (isAssemblyMateAutoAngleReferenceEntity(entity)) {
    return {
      entity: cloneSerializable(entity),
    };
  }

  if (!entity.topologyRef && !entity.referenceRef) {
    return {
      entity: cloneSerializable(entity),
      missingReferenceMessage: `配合引用缺少稳定选择引用，无法恢复：${entity.assemblyNodeId}`,
    };
  }

  const resolvedEntity = resolveEntity(cloneSerializable(entity));
  if (!resolvedEntity) {
    return {
      entity: cloneSerializable(entity),
      missingReferenceMessage: `配合引用无法通过稳定选择引用恢复：${entity.assemblyNodeId}`,
    };
  }

  return {
    entity: preserveAssemblyMateDocumentEntityIdentity(entity, resolvedEntity),
  };
}

/**
 * 恢复单个 Mate Definition 的实体引用。
 *
 * @param mate 持久化 Mate Definition。
 * @param resolveEntity 当前模型稳定引用解析函数。
 * @returns 恢复后的 Mate Definition。
 */
function restoreMateDefinitionReferences(
  mate: AssemblyMateDefinition,
  resolveEntity: AssemblyMateEntityReferenceResolver,
): AssemblyMateDefinition {
  const firstResult = restoreMateEntityReference(mate.firstEntity, resolveEntity);
  const secondResult = restoreMateEntityReference(mate.secondEntity, resolveEntity);
  const referenceResult = mate.referenceEntity
    ? restoreMateEntityReference(mate.referenceEntity, resolveEntity)
    : null;
  const errorMessage = firstResult.missingReferenceMessage
    ?? secondResult.missingReferenceMessage
    ?? referenceResult?.missingReferenceMessage;

  return {
    ...cloneAssemblyMateDefinition(mate),
    firstEntity: firstResult.entity,
    secondEntity: secondResult.entity,
    ...(referenceResult?.entity ?? mate.referenceEntity
      ? { referenceEntity: referenceResult?.entity ?? mate.referenceEntity }
      : {}),
    solveStatus: errorMessage ? MateSolveStatus.MissingReference : MateSolveStatus.NotRun,
    errorMessage,
    updatedAt: mate.updatedAt,
  };
}

/**
 * 从持久化数据恢复装配体 Mate 文档。
 *
 * @param data 持久化数据。
 * @returns 恢复后的 Mate 文档。
 * @throws 当 Mate 文档版本或任一 Mate Definition 版本不受支持时抛出错误。
 */
export function restoreAssemblyMateDocument(
  data: AssemblyMateDocumentModel,
): AssemblyMateDocumentModel {
  if (data.schemaVersion !== ASSEMBLY_MATE_DOCUMENT_SCHEMA_VERSION) {
    throw new Error(`不支持的装配体配合文档版本：${data.schemaVersion}`);
  }
  data.mates.forEach((mate) => {
    if (mate.schemaVersion !== ASSEMBLY_MATE_SCHEMA_VERSION) {
      throw new Error(`不支持的装配体配合定义版本：${mate.schemaVersion}`);
    }
  });

  return serializeAssemblyMateDocument(data);
}

/**
 * 通过稳定选择引用重新解析并刷新 Mate 文档中的实体几何。
 *
 * 设计说明：
 * Mate 持久化保存的是稳定拓扑/基准引用和选择瞬间几何快照。重载后必须优先用当前模型重新解析几何，
 * 防止零件重载、拓扑 sidecar 刷新、基准定义变化或单位转换后继续使用陈旧 world space 几何。该函数只消费
 * plain data 和注入的解析函数，不依赖运行时 Object3D。
 *
 * @param input 引用恢复输入。
 * @returns 刷新引用后的 Mate 文档。
 */
export function restoreAssemblyMateDocumentReferences(
  input: RestoreAssemblyMateDocumentReferencesInput,
): AssemblyMateDocumentModel {
  const restoredDocument = restoreAssemblyMateDocument(input.document);
  return {
    ...restoredDocument,
    mates: restoredDocument.mates.map((mate) => (
      restoreMateDefinitionReferences(mate, input.resolveEntity)
    )),
    /** 引用恢复属于加载阶段，不是用户编辑，不能污染持久化更新时间。 */
    updatedAt: restoredDocument.updatedAt,
  };
}
